/**
 * Captures the browser's install prompt (Android/Chrome/Edge) and detects
 * the iOS Safari case, where no automatic prompt exists and the user must
 * be guided through the manual "Add to Home Screen" flow.
 */
import { useEffect, useState, useCallback } from "react";

const DISMISS_STORAGE_KEY = "bubakii-os-install-dismissed-at";
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isStandaloneDisplay() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
}

function isSafariBrowser() {
  return (
    /Safari/.test(window.navigator.userAgent) &&
    !/CriOS|FxiOS|OPiOS|EdgiOS/.test(window.navigator.userAgent)
  );
}

function wasRecentlyDismissed() {
  const dismissedAt = Number(localStorage.getItem(DISMISS_STORAGE_KEY) || 0);
  return Date.now() - dismissedAt < DISMISS_COOLDOWN_MS;
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandaloneDisplay());
  const [dismissed, setDismissed] = useState(wasRecentlyDismissed());

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
    setDismissed(true);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const canInstallAndroid = !!deferredPrompt && !installed && !dismissed;
  const showIOSInstructions =
    isIOSDevice() && isSafariBrowser() && !installed && !dismissed;

  return { canInstallAndroid, showIOSInstructions, installed, promptInstall, dismiss };
}
